import classNames from 'classnames'
import type { FC } from 'react'
import React, { useEffect, useMemo, useRef } from 'react'
import type { FlexboxProps } from 'styled-system'

import type { BadgeProps, BadgeStatefulThemeProps } from './types'
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/rules-of-hooks */
import type { Theme } from '../../theme-provider/types'
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui'
import isReact from '../../utils/is-react'
import { toNumber } from '../../utils/number'
import { get } from '../../utils/object'
import {
  containsOmitPropSymbol,
  getPaddingX,
  getPaddingY,
  getSpace,
  omitSpace
} from '../../utils/props-utils'
import { resolveSize } from '../../utils/size-utils'
import { FlexPrivate } from '../_primitives/flex/flex'
import { FlexInlinePrivate } from '../_primitives/flex/flex-inline'
import Text from '../_primitives/text/text'

function usePreviousProps<T extends object = {}>(value: T): T {
  const ref = useRef<T>({} as T)
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const BadgeIndicator = React.forwardRef<
  HTMLElement,
  FlexboxProps & BadgeStatefulThemeProps
>(function BadgeIndicator(props, ref) {
  // @ts-ignore
  return <FlexPrivate ref={ref} {...props} />
})

const Badge: FC<BadgeProps> = React.forwardRef(function Badge(
  props: BadgeProps & { theme?: Theme },
  ref: React.Ref<HTMLDivElement>
) {
  const theme = useSafeThemeUI(props)
  const {
    display,
    type: typeProp = 'standard',
    variant = 'default',
    anchorOrigin: anchorOriginProp = {
      vertical: 'top',
      horizontal: 'right'
    },
    badgeContent: badgeContentProp,
    max: maxProp = 99,
    showZero = false,
    overlap: overlapProp = 'rectangular',
    as: asProps = 'span',
    invisible: invisibleProp,
    children,
    size: sizeProps,
    borderRadius = 'pill',
    sx,
    enableClickableStyles,
    ...rest
  } = props

  const size = !sizeProps && children ? 'md' : undefined

  const _fontSize = resolveSize(size, get(theme, 'sizes.font'))
  const combinePadding = {
    ...getPaddingX(props),
    ...getPaddingY(props),
    ...getPaddingX(sx as any),
    ...getPaddingY(sx as any)
  }

  const paddingProps =
    containsOmitPropSymbol(combinePadding) || isReact.element(badgeContentProp)
      ? {}
      : {
          emPaddingX: 'xs-2',
          ...combinePadding
        }

  const prevProps = usePreviousProps({
    anchorOrigin: anchorOriginProp,
    badgeContent: badgeContentProp,
    max: maxProp,
    overlap: overlapProp,
    type: typeProp
  })

  let invisible = invisibleProp

  if (
    invisibleProp == null &&
    ((badgeContentProp === 0 && !showZero) ||
      (badgeContentProp == null && typeProp !== 'dot'))
  ) {
    invisible = true
  }

  const {
    anchorOrigin = anchorOriginProp,
    badgeContent,
    max = maxProp,
    overlap = overlapProp,
    type = typeProp
  } = invisible ? prevProps : props

  let displayValue: React.ReactNode = ''

  if (type !== 'dot') {
    displayValue = toNumber(badgeContent) > max ? `${max}+` : badgeContent
  }

  const spaceProps = getSpace(rest)

  const textProps = useMemo(
    () => ({
      fontSize: !children ? '0.75em' : '0.6em',
      sx: {
        ...(enableClickableStyles
          ? {
              cursor: 'pointer',
              '-webkit-user-select': 'none' /* Safari */,
              '-ms-user-select': 'none' /* IE 10 and IE 11 */,
              'user-select': 'none' /* Standard syntax */
              // '&:hover': {
              //   /* Apply this to BadgeIndicator ONLY(not needed atm) */
              //   opacity: 0.45
              // }
            }
          : null)
      }
    }),
    [children, enableClickableStyles]
  )

  return (
    <FlexInlinePrivate
      display={display}
      ref={ref}
      as={asProps}
      relative
      alignItems="center"
      flexShrink={0}
      className={'csg-tui-badge'}
      fontSize={_fontSize}
      width={'fit-content'}
      {...(!children && invisible ? { display: 'none' } : spaceProps)}
    >
      {children || (
        <FlexInlinePrivate
          as="span"
          color="transparent"
          maxHeight=".8em"
          emPaddingX="xs-3"
        >
          {toNumber(displayValue) < 10
            ? `0${displayValue}` // need a 2 digit placeholder
            : displayValue}
        </FlexInlinePrivate>
      )}
      <BadgeIndicator
        as="span"
        __themeKey="badges"
        variant={variant}
        className={classNames({
          'csg-tui-badge-indicator': true,
          dot: type === 'dot',
          invisible: invisible
        })}
        sx={textProps.sx}
        anchorOrigin={!!children && anchorOrigin}
        overlap={overlap}
        invisible={invisible}
        absolute
        fontWeight="body"
        justifyContent="center"
        alignContent="center"
        alignItems="center"
        zIndex={50} // Render the badge on top of potential ripples.
        borderRadius={borderRadius}
        {...{
          ...(!children && invisible
            ? {}
            : {
                minWidth: !children ? '1.3em' : '1em',
                height: !children ? '1.3em' : '1em',
                ...(!children ? { top: '-35%', left: '10%' } : {}),
                ...paddingProps
              }),
          ...omitSpace(rest)
        }}
      >
        {!isReact.element(displayValue) ? (
          <Text {...textProps}>{displayValue}</Text>
        ) : (
          displayValue
        )}
      </BadgeIndicator>
    </FlexInlinePrivate>
  )
})

export default Badge
